import React, { forwardRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { CircularProgress } from '@material-ui/core';
import { useDsTokens } from '../theme/useDsTokens';
import { cn } from '../utils/cn';
import { DsPrimitiveProps, SpinnerSize, SpinnerVariant } from './types';

export interface SpinnerProps extends DsPrimitiveProps {
  size?: SpinnerSize;
  variant?: SpinnerVariant;
  /** When true, uses currentColor instead of brand primary */
  inheritColor?: boolean;
  /** Screen-reader label — default "Carregando" */
  label?: string;
  /** Hide from a11y tree when parent announces busy state (e.g. Button loading) */
  decorative?: boolean;
}

const SIZE_MAP: Record<SpinnerSize, number> = {
  sm: 16,
  md: 24,
  lg: 40,
};

const useCenteredStyles = makeStyles(() => ({
  centered: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    padding: 24,
  },
}));

const srOnlyStyle: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export const Spinner = forwardRef<HTMLSpanElement, SpinnerProps>(function Spinner(
  {
    id,
    className,
    size = 'md',
    variant = 'inline',
    inheritColor = false,
    label = 'Carregando',
    decorative = false,
    'data-testid': testId,
  },
  ref
) {
  const tokens = useDsTokens();
  const centeredClasses = useCenteredStyles();
  const dimension = SIZE_MAP[size];

  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const a11yProps = decorative
    ? { 'aria-hidden': true as const }
    : {
        role: 'progressbar' as const,
        'aria-label': label,
      };

  const spinner = prefersReducedMotion ? (
    <span
      ref={ref}
      id={id}
      {...a11yProps}
      className={cn(className)}
      data-testid={testId}
      style={{
        fontSize: tokens.fontSizeCaption,
        color: inheritColor ? 'currentColor' : tokens.brandPrimary,
      }}
    >
      {!decorative && label}
      {!decorative && '…'}
    </span>
  ) : (
    <span
      ref={ref}
      id={id}
      {...a11yProps}
      className={cn(className)}
      data-testid={testId}
      style={{ display: 'inline-flex', lineHeight: 0, position: 'relative' }}
    >
      <CircularProgress
        size={dimension}
        thickness={size === 'sm' ? 4 : 3.6}
        style={{
          color: inheritColor ? 'currentColor' : tokens.brandPrimary,
          animationDuration: `${tokens.motionDurationSkeleton}ms`,
        }}
      />
      {!decorative && <span style={srOnlyStyle}>{label}</span>}
    </span>
  );

  if (variant === 'centered') {
    return <div className={centeredClasses.centered}>{spinner}</div>;
  }

  return spinner;
});

Spinner.displayName = 'Spinner';
